/*
 * DrillHeader
 *
 * Header above the drill list at the Voyages/Routes levels (the Vessels
 * level has none), one row: back arrow + vessel name + info icon (toggles
 * the specs view) with the meta line (voyage count / voyage name) below,
 * and the route-table button on the right. The selection itself shows only
 * as highlighted rows in the list. While the specs view is open the arrow
 * closes it and the table button is hidden. Presentational.
 */

import React from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { WW_BLUE } from "@/theme/tokens";
import {
  PANEL_BUTTON_SX,
  PANEL_TITLE_SX,
  PANEL_SUBTITLE_SX,
} from "@/theme/panelStyles";

const ELLIPSIS_SX = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export default function DrillHeader({
  title,
  meta,
  onBack,
  backLabel = "Back",
  canCompare = false,
  onCompare,
  specsOpen = false,
  onToggleSpecs,
}) {
  // With the specs open, the arrow only closes them — the drill level stays.
  const handleBack = specsOpen ? onToggleSpecs : onBack;
  const arrowLabel = specsOpen ? "Close vessel specifications" : backLabel;

  return (
    // No side padding: Back and Table sit on the card padding, like the
    // specs view's Edit button.
    <Box
      data-tour="fleet-drill-header"
      sx={{ py: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}
    >
      <Tooltip title={arrowLabel}>
        <Button
          variant="contained"
          onClick={handleBack}
          aria-label={arrowLabel}
          sx={{ ...PANEL_BUTTON_SX, minWidth: 0, px: 0.75 }}
        >
          {/* One text line high, so Back matches Table's height. */}
          <Box
            component="span"
            sx={{ display: "flex", alignItems: "center", height: "1.75em" }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </Box>
        </Button>
      </Tooltip>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={title} placement="bottom-start">
            <Typography
              sx={{
                ...PANEL_TITLE_SX,
                lineHeight: 1.25,
                minWidth: 0,
                ...ELLIPSIS_SX,
              }}
            >
              {title}
            </Typography>
          </Tooltip>
          {onToggleSpecs ? (
            <Tooltip title="Vessel specifications">
              <IconButton
                size="small"
                onClick={onToggleSpecs}
                aria-label="Vessel specifications"
                sx={{ p: 0.25, color: WW_BLUE, flexShrink: 0 }}
              >
                <InfoIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
        {meta ? (
          <Tooltip title={meta} placement="bottom-start">
            <Typography
              sx={{
                ...PANEL_SUBTITLE_SX,
                lineHeight: 1.25,
                ...ELLIPSIS_SX,
              }}
            >
              {meta}
            </Typography>
          </Tooltip>
        ) : null}
      </Box>
      {!specsOpen && (
        <Button
          data-tour="fleet-table-button"
          variant="contained"
          onClick={onCompare}
          disabled={!canCompare}
          startIcon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            ...PANEL_BUTTON_SX,
            "&.Mui-disabled": { backgroundColor: "#b6c2cd", color: "white" },
          }}
        >
          Table
        </Button>
      )}
    </Box>
  );
}
